import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAnalytics } from '../context/AnalyticsContext'
import { useAuth } from '../context/AuthContext'
import { useReadChats } from '../context/ReadChatsContext'
import { loadChats } from '../lib/chats'
import { supabase } from '../lib/supabase'
import Loader from './Loader'
import './Chats.css'

const Chats = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const { trackEvent } = useAnalytics()
  const { user, profile } = useAuth()
  const { isChatRead, setHasUnreadMessages } = useReadChats()
  
  const hasSubscription = profile?.subscription_status === 'active' || profile?.subscription_status === 'trial'
  const [chats, setChats] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user?.id) {
      loadChatsList()
      const unsubscribe = subscribeToChats()
      return () => {
        if (unsubscribe) unsubscribe()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  // Перезагружаем список чатов при возврате на экран списка (без смены ссылки user)
  useEffect(() => {
    if (user?.id && location.pathname === '/app/chats') {
      loadChatsList({ quiet: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user?.id])

  const loadChatsList = async (opts = {}) => {
    if (!user?.id) return
    const quiet = opts.quiet === true
    if (!quiet) setLoading(true)
    try {
      const chatsList = await loadChats(user.id)
      setChats(chatsList)
      const hasUnread = chatsList.some((c) => (c.unreadCount || 0) > 0)
      setHasUnreadMessages(hasUnread)
    } catch (error) {
      console.error('[Chats] Error loading chats:', error)
    } finally {
      if (!quiet) setLoading(false)
    }
  }

  const subscribeToChats = () => {
    if (!user) return

    // Подписываемся на новые сообщения во всех чатах пользователя
    const channel = supabase
      .channel('chats-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        async (payload) => {
          const newMsg = payload?.new
          if (!newMsg) return
          
          // Обновляем список только если сообщение от другого пользователя
          if (newMsg.sender_id === user.id) return
          
          const chatId = newMsg.chat_id
          // Проверяем, относится ли чат к текущему пользователю
          const { data: chatData } = await supabase
            .from('chats')
            .select('user1_id, user2_id')
            .eq('id', chatId)
            .single()
          
          if (chatData && (chatData.user1_id === user.id || chatData.user2_id === user.id)) {
            // Перезагружаем список чатов чтобы показать новое сообщение
            loadChatsList({ quiet: true })
          }
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }

  const handleGetAstroPlus = () => {
    trackEvent('get_astro_plus_clicked', { location: 'chats' })
    navigate('/paywall?reason=subscription', { state: { from: location.pathname } })
  }

  const handleChatClick = (chat) => {
    trackEvent('chat_opened', { chatId: chat.id })
    navigate(`/chat/${chat.id}`)
  }

  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    } else if (diffDays === 1) {
      return t('chats.yesterday')
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' })
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    }
  }

  if (loading) {
    return (
      <div className="chats-screen">
        <div className="chats-top-bar">
          <button type="button" className="logo-large logo-btn" onClick={() => navigate('/app')} aria-label="Go to Explore">
            <img src="/Logo.png" alt="AstroMatch Logo" />
          </button>
          {!hasSubscription && (
            <button className="astro-plus-btn" onClick={handleGetAstroPlus}>
              {t('chats.getAstroPlus')}
            </button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh' }}>
          <Loader text={t('common.loading')} />
        </div>
      </div>
    )
  }

  return (
    <div className="chats-screen">
      <div className="chats-top-bar">
        <button type="button" className="logo-large logo-btn" onClick={() => navigate('/app')} aria-label="Go to Explore">
          <img src="/Logo.png" alt="AstroMatch Logo" />
        </button>
        {!hasSubscription && (
          <button className="astro-plus-btn" onClick={handleGetAstroPlus}>
            {t('chats.getAstroPlus')}
          </button>
        )}
      </div>

      {chats.length === 0 ? (
        <div className="chats-empty-state">
          <div style={{ fontSize: '48px', marginBottom: '24px' }}>💬</div>
          <h2 className="empty-title">{t('chats.emptyTitle')}</h2>
          <p className="empty-description">
            {t('chats.emptyDesc')}
          </p>
        </div>
      ) : (
        <div className="chats-list">
          {chats.map((chat) => {
            // Используем unreadCount из БД напрямую - он всегда актуален
            const unreadCount = chat.unreadCount || 0
            const showUnread = unreadCount > 0
            return (
            <div
              key={chat.id}
              className="chat-item"
              onClick={() => handleChatClick(chat)}
            >
              <div className="chat-avatar">
                {chat.otherUserPhoto ? (
                  <img src={chat.otherUserPhoto} alt={chat.otherUserName} />
                ) : (
                  <div className="chat-avatar-placeholder">
                    {chat.otherUserName?.[0] || '?'}
                  </div>
                )}
              </div>
              <div className="chat-content">
                <div className="chat-header">
                  <div className="chat-name-wrapper">
                    <span className="chat-name">{chat.otherUserName}</span>
                    {showUnread && (
                      <span className="chat-unread-badge">{unreadCount}</span>
                    )}
                  </div>
                  <span className="chat-date">{formatDate(chat.lastMessageDate)}</span>
                </div>
                <div className="chat-preview">
                  {chat.lastMessage ? (
                    <span className={showUnread ? 'chat-message-unread' : ''}>
                      {chat.lastMessage}
                    </span>
                  ) : (
                    <span className="chat-message-empty">{t('chats.startChat')}</span>
                  )}
                </div>
              </div>
            </div>
          )})}
        </div>
      )}
    </div>
  )
}

export default Chats

